import { useEffect, useRef, useState } from 'react';
import type { ComponentProps } from 'react';
import type { DetailPageButtons } from '@modules/business-manager/component/detail-page-buttons/DetailPageButtons';
import { useAppData } from '~/modules/root/hook/use-app-data';
import { useSetInLibrary } from '~/modules/directory/hook/use-set-in-library';
import { useToggleFeaturedProject } from '~/modules/directory/hook/use-toggle-featured-project';
import { toast } from 'sonner';
import { isDirectoryAdmin } from '@modules/project/util/is-directory-admin';

type DetailPageButtonsProps = ComponentProps<typeof DetailPageButtons>;

interface AdminToggleProject {
    ID: number;
    InLibrary?: boolean | null;
    Professional?: {
        InLibrary?: boolean | null;
        Disable?: boolean | null;
    } | null;
}

/**
 * The AP-admin "in directory" and "pin to top" toggles for a project detail
 * page, with optimistic state and result toasts. Returns `undefined` toggles
 * for non-admin users. Shared by the legacy page and the details revamp so
 * both variants expose the same admin controls.
 */
export const useProjectDirectoryAdminToggles = (
    project: AdminToggleProject,
    isPinnedToDirectory: boolean,
): Pick<DetailPageButtonsProps, 'visibilityToggle' | 'pinToggle'> => {
    const { user } = useAppData();
    const isAdminUser = isDirectoryAdmin(user);

    const [optimisticInLibrary, setOptimisticInLibrary] = useState(
        !!project.InLibrary,
    );
    useEffect(() => {
        setOptimisticInLibrary(!!project.InLibrary);
    }, [project.InLibrary]);
    const [optimisticPinned, setOptimisticPinned] =
        useState(isPinnedToDirectory);
    useEffect(() => {
        setOptimisticPinned(isPinnedToDirectory);
    }, [isPinnedToDirectory]);
    // Captures whether the project was pinned when a remove-from-directory
    // request was submitted, so the success toast can mention the auto-unpin
    const wasPinnedOnRemovalRef = useRef(false);

    const { setInLibrary } = useSetInLibrary(
        (error) => {
            // A failed removal must not leave the ref primed, or a later
            // successful removal would falsely toast the auto-unpin copy.
            wasPinnedOnRemovalRef.current = false;
            setOptimisticInLibrary(!!project.InLibrary);
            setOptimisticPinned(isPinnedToDirectory);
            toast.error(error.Message || 'Something went wrong');
        },
        (_, __, inLibrary) =>
            toast.success(
                inLibrary
                    ? 'Added to directory.'
                    : wasPinnedOnRemovalRef.current
                      ? 'Removed from directory and unpinned.'
                      : 'Removed from directory.',
            ),
    );

    const { toggleFeaturedProject } = useToggleFeaturedProject(
        (error) => {
            setOptimisticPinned(isPinnedToDirectory);
            toast.error(error.Message || 'Something went wrong');
        },
        (_, pinned) =>
            toast.success(
                pinned
                    ? 'Pinned. This project will now show at the top of the projects directory.'
                    : 'Unpinned from the projects directory.',
            ),
    );

    return {
        visibilityToggle:
            isAdminUser && setInLibrary
                ? {
                      isInDirectory: optimisticInLibrary,
                      disabled:
                          !project.Professional?.InLibrary ||
                          !!project.Professional?.Disable,
                      itemType: 'project',
                      removalNote: optimisticPinned
                          ? 'It will also be unpinned from the top of the directory.'
                          : undefined,
                      onToggle: (inLibrary) => {
                          wasPinnedOnRemovalRef.current =
                              !inLibrary && optimisticPinned;
                          setOptimisticInLibrary(inLibrary);
                          // Backend auto-unpins projects leaving the directory
                          if (!inLibrary) {
                              setOptimisticPinned(false);
                          }
                          setInLibrary('project', project.ID, inLibrary);
                      },
                  }
                : undefined,
        pinToggle:
            isAdminUser && toggleFeaturedProject
                ? {
                      isPinned: optimisticPinned,
                      isInDirectory: optimisticInLibrary,
                      onToggle: (pinned) => {
                          setOptimisticPinned(pinned);
                          toggleFeaturedProject(project.ID, pinned);
                      },
                  }
                : undefined,
    };
};
